import { motion } from "framer-motion";
import { useLang } from "@/i18n/LangContext";
import { useContent } from "@/context/SiteContext";
import { useDarkHero } from "@/context/UIContext";
import { Container, Eyebrow } from "@/components/common";
import { BookingBar } from "./BookingBar";

const HERO_IMG = "/images/exterior/fachada.webp";

export function Hero() {
  useDarkHero();
  const { t, lang } = useLang();
  const c = useContent();
  const titleLines = c("hero_title", lang, "Hotel en Martorell,\na un paso de Barcelona").split("\n");

  return (
    <section data-testid="home-hero" className="relative bg-sand">
      <div className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden bg-ink text-bone">
        <motion.img
          src={HERO_IMG}
          alt="Hotel Ciutat Martorell"
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
          fetchPriority="high"
        />
        <div className="absolute inset-0 scrim-b" />

        <div className="pointer-events-none absolute right-6 top-1/2 hidden lg:flex -translate-y-1/2 rotate-90 items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/50">
          <span className="h-px w-10 bg-bone/40" /> {t.hero.scroll}
        </div>

        <Container className="relative pb-10 md:pb-16 pt-44">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}>
            <Eyebrow light className="mb-6">{c("hero_eyebrow", lang, "Hotel 4 estrellas · Martorell, Barcelona")}</Eyebrow>
          </motion.div>
          <h1 className="font-serif text-[2.75rem] leading-[1.02] sm:text-6xl lg:text-7xl xl:text-[5.5rem] tracking-tight max-w-5xl">
            {titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
                <motion.span className={`block ${i === 1 ? "italic font-light text-bone/90" : ""}`} initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.45 + i * 0.12 }}>
                  {line}
                </motion.span>
                {i < titleLines.length - 1 && " "}
              </span>
            ))}
          </h1>
          <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="max-w-md text-base md:text-lg leading-relaxed text-bone/75">
              {c("hero_subtitle", lang)}
            </motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }} className="hidden sm:flex gap-10 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/60">
              <span><span className="block font-serif text-3xl text-bone not-italic tracking-tight">92</span>{t.pages.homeHeadings.heroRooms}</span>
              <span><span className="block font-serif text-3xl text-bone tracking-tight">100 m</span>Renfe · FGC</span>
              <span><span className="block font-serif text-3xl text-bone tracking-tight">30 min</span>Barcelona</span>
            </motion.div>
          </div>
        </Container>
      </div>

      <Container className="relative -mt-2 md:-mt-14 z-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}>
          <BookingBar />
        </motion.div>
      </Container>
    </section>
  );
}
